'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { InsertImageIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Big } from 'foundations-components/transitional/components/Big';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Image } from 'foundations-components/transitional/components/Image';
import { Link } from 'foundations-components/transitional/components/Link';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tab, Tabs } from 'foundations-components/transitional/components/TabsTransitionalOld';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import PropTypes from 'prop-types';
import { Fragment, createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import * as AppContext from '../../constants/AppContext';
import { LOGO_IMAGE_HEIGHT, PROFILE_LEFT_COL_SIZE, PROFILE_RIGHT_COL_SIZE } from '../../constants/ProfileConstants';
import { getIsCustomer } from '../../selectors/auth';
import { getServices } from '../../slices/serviceCatalogSlice';
import { getMultiSelectStorefrontFilters } from '../../slices/storefrontFiltersSlice';
import { getRecentActivity } from '../../utils/homepage';
import { fromFilterStateToLocation } from '../../utils/storefrontFilters';
import { getEditProfileLink, getFullLocationUrl } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_BASE = 'ecosystem-marketplace-solutions-ui.profile';
const LOGO_IMAGE_STICKY_HEIGHT = 25;
const NAV_MIN_SCREEN_SIZE = ScreenSizes.MD;
const START_GRADIENT_COLOR = '#00497D';
const END_GRADIENT_COLOR = '#0FBFBF';
const ResponsiveMainSection = props => /*#__PURE__*/_jsx(MainSection, Object.assign({}, props, {
  responsive: true
}));
const BreadcrumbsWrapper = styled(ResponsiveMainSection).withConfig({
  displayName: "ProfileHeader__BreadcrumbsWrapper",
  componentId: "j3jcj7-0"
})(["background-image:linear-gradient( -303deg,", ",", " );"], START_GRADIENT_COLOR, END_GRADIENT_COLOR);
const StickyHeaderWrapper = styled(ResponsiveMainSection).withConfig({
  displayName: "ProfileHeader__StickyHeaderWrapper",
  componentId: "j3jcj7-1"
})(["position:fixed;top:0;left:", ";z-index:1211;padding-top:0;padding-bottom:0;width:", ";border-bottom:1px solid ", ";"], props => props.isInApp ? '236px' : 0, props => props.isInApp ? 'calc(100% - var(--global-nav-vertical-nav-width, 236px))' : '100%', ({
  theme
}) => theme.color['border-core-default']);
const ProfileHeader = ({
  activeTab,
  canonicalAppContext,
  isPrerender,
  isPreview,
  onTrackInteraction,
  profileListing,
  scrollToTabSection
}) => {
  const {
    logoUrl,
    name,
    portalId: providerPortalId
  } = profileListing;
  const isCustomer = useSelector(getIsCustomer);
  const storefrontFilters = useSelector(getMultiSelectStorefrontFilters);
  const catalogServices = useSelector(getServices);
  const tabsRef = /*#__PURE__*/createRef();
  const [tabsOffsetTop, setTabsOffsetTop] = useState(null);
  const [sticky, setSticky] = useState(false);
  const history = useHistory();
  const handleTrackInteraction = useCallback((action, extras = {}) => {
    onTrackInteraction(action, Object.assign({}, extras, {
      section: 'header'
    }));
  }, [onTrackInteraction]);
  const renderedBreadcrumbs = useMemo(() => {
    const filtersLocation = fromFilterStateToLocation(storefrontFilters, catalogServices);
    const renderedBreadcrumbText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${I18N_BASE}.back`
    });
    const fromHomepage = history.location.state && history.location.state.fromHomepage;
    let renderedBreadcrumbLink;
    if (isPrerender || isPreview) {
      renderedBreadcrumbLink = /*#__PURE__*/_jsx(Link, {
        disabled: isPreview,
        href: getFullLocationUrl(filtersLocation, canonicalAppContext),
        use: "on-dark",
        children: renderedBreadcrumbText
      });
    } else {
      const recentActivity = getRecentActivity();
      const useQuestionnaireRef = recentActivity && recentActivity.type === 'questionnaire';
      if (useQuestionnaireRef) {
        filtersLocation.search = filtersLocation.search.concat('&ref=questionnaire');
      }
      renderedBreadcrumbLink = /*#__PURE__*/_jsx(UIRouterLink, {
        to: fromHomepage && '/' || filtersLocation // if coming from Homepage, redirect accordingly
        ,
        use: "on-dark",
        onClick: () => handleTrackInteraction('click breadcrumb link'),
        children: renderedBreadcrumbText
      });
    }
    return /*#__PURE__*/_jsx(Breadcrumbs, {
      paddingInline: "0",
      marginBlockStart: "0",
      children: renderedBreadcrumbLink
    });
  }, [canonicalAppContext, catalogServices, handleTrackInteraction, history, isPrerender, isPreview, storefrontFilters]);
  const renderedEditButton = useMemo(() => {
    const editProfileHref = getEditProfileLink(providerPortalId);
    const editButton = /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Button, {
        href: editProfileHref,
        use: "primary-white",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_BASE}.edit`
        })
      })
    });
    return !isPreview && providerPortalId === PortalIdParser.get() ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MediaQueryView, {
        min: NAV_MIN_SCREEN_SIZE,
        children: /*#__PURE__*/_jsx(View, {
          position: "relative",
          insetBlockStart: "800",
          children: editButton
        })
      }), /*#__PURE__*/_jsx(MediaQueryView, {
        max: NAV_MIN_SCREEN_SIZE,
        children: editButton
      })]
    }) : null;
  }, [isPreview, providerPortalId]);
  const handleTabClick = useCallback(tabId => {
    scrollToTabSection(tabId);
    handleTrackInteraction('click tab navigation', {
      value: tabId
    });
  }, [handleTrackInteraction, scrollToTabSection]);
  const renderedTabs = useMemo(() => {
    return /*#__PURE__*/_jsx(MediaQueryView, {
      min: NAV_MIN_SCREEN_SIZE,
      children: /*#__PURE__*/_jsxs(Tabs, {
        selected: activeTab,
        paddingBlockStart: "400",
        children: [/*#__PURE__*/_jsx(Tab, {
          tabId: "about",
          onClick: () => handleTabClick('about'),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_BASE}.tabs.about`
          })
        }), /*#__PURE__*/_jsx(Tab, {
          tabId: "reviews",
          onClick: () => handleTabClick('reviews'),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_BASE}.tabs.reviews`
          })
        })]
      })
    });
  }, [activeTab, handleTabClick]);
  const renderedProfileImage = useMemo(() => {
    return logoUrl ? /*#__PURE__*/_jsx(Image, {
      alt: name,
      src: logoUrl,
      objectFit: "contain",
      blockSize: sticky ? LOGO_IMAGE_STICKY_HEIGHT : LOGO_IMAGE_HEIGHT
    }) : /*#__PURE__*/_jsxs(Flex, {
      height: "100%",
      direction: sticky ? 'row' : 'column',
      align: "center",
      alignItems: "center",
      justify: "center",
      gap: "200",
      children: [/*#__PURE__*/_jsx(InsertImageIcon, {
        size: sticky ? '400' : '800',
        color: "icon-core-subtle"
      }), /*#__PURE__*/_jsx(Big, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_BASE}.logoPlaceholder`
        })
      })]
    });
  }, [logoUrl, name, sticky]);
  const scrollHandler = useCallback(() => requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setSticky(tabsOffsetTop !== null && (document.body.classList.contains('sticky-global-toolbar') ? document.getElementsByClassName('page')[0].scrollTop : window.pageYOffset) >= tabsOffsetTop);
    });
  }), [tabsOffsetTop]);
  useEffect(() => {
    const {
      top: tabsTop
    } = tabsRef.current.getBoundingClientRect();
    setTabsOffsetTop(tabsTop + (document.body.classList.contains('sticky-global-toolbar') ? document.getElementsByClassName('page')[0].scrollTop : window.pageYOffset));
  }, [tabsRef]);
  useEffect(() => {
    scrollHandler();
    window.addEventListener('scroll', scrollHandler, true);
    return () => window.removeEventListener('scroll', scrollHandler, true);
  }, [scrollHandler]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(BreadcrumbsWrapper, {
      paddingBlockStart: "0",
      paddingBlockEnd: "1400",
      children: /*#__PURE__*/_jsx(View, {
        paddingBlock: "400",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(TFlex, {
            justify: "between",
            children: [renderedBreadcrumbs, renderedEditButton]
          })
        })
      })
    }), /*#__PURE__*/_jsx(ResponsiveMainSection, {
      color: "light",
      position: "relative",
      marginBlockEnd: `-1400`,
      paddingBlock: "0",
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(Grid, {
          children: [/*#__PURE__*/_jsx(GridItem, {
            size: PROFILE_LEFT_COL_SIZE,
            children: /*#__PURE__*/_jsx(View, {
              position: "relative",
              insetBlockEnd: "1200",
              children: /*#__PURE__*/_jsx(CardWrapper, {
                children: /*#__PURE__*/_jsx(Flex, {
                  alignItems: "center",
                  justifyContent: "center",
                  paddingInline: "1000",
                  paddingBlock: "400",
                  children: renderedProfileImage
                })
              })
            })
          }), /*#__PURE__*/_jsx(GridItem, {
            size: PROFILE_RIGHT_COL_SIZE,
            children: /*#__PURE__*/_jsx("div", {
              ref: tabsRef,
              children: renderedTabs
            })
          })]
        })
      })
    }), sticky && /*#__PURE__*/_jsx(MediaQueryView, {
      min: NAV_MIN_SCREEN_SIZE,
      children: /*#__PURE__*/_jsx(StickyHeaderWrapper, {
        isInApp: isCustomer,
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(Grid, {
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              flex: 1,
              children: [/*#__PURE__*/_jsx(GridItem, {
                size: PROFILE_LEFT_COL_SIZE,
                children: renderedProfileImage
              }), /*#__PURE__*/_jsx(GridItem, {
                size: PROFILE_RIGHT_COL_SIZE,
                children: renderedTabs
              })]
            })
          })
        })
      })
    })]
  });
};
ProfileHeader.propTypes = {
  activeTab: PropTypes.oneOf(['about', 'reviews']).isRequired,
  canonicalAppContext: PropTypes.oneOf(Object.values(AppContext)).isRequired,
  isPrerender: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  scrollToTabSection: PropTypes.func.isRequired
};
export default ProfileHeader;